import { autorun, isObservableArray, isObservableMap, isObservableObject, runInAction } from 'mobx';
import { useEffect, useState } from 'react';

export type ProxyStore<T> = {
  [K in keyof T]: T[K];
};

// proxy to read and update the mobx store
export const useProxyStore = <T>(mobxStore: T): ProxyStore<T> => {
  const [, setTrigger] = useState(0); // Dummy state to force re-renders

  // Explicitly access all properties, including functions (computeds), observable arrays, maps, and objects
  function deepAccess(obj: any) {
    if (isObservableArray(obj) || isObservableMap(obj)) {
      obj.forEach(deepAccess);
    } else if (isObservableObject(obj)) {
      Object.entries(obj).forEach(([key, value]) => {
        // Access computed properties (getters) as properties, not functions
        const descriptor = Object.getOwnPropertyDescriptor(obj, key);
        if (descriptor && typeof descriptor.get === 'function') {
          obj[key];
        } else if (typeof value !== 'function') {
          deepAccess(value);
        }
      });
    }
  }

  useEffect(() => {
    const disposer = autorun(() => {
      deepAccess(mobxStore);
      setTrigger((prev) => prev + 1); // Trigger a re-render
    });

    return disposer;
  }, [mobxStore]);

  const objStore = mobxStore as any;

  const proxyStore = new Proxy(objStore, {
    set(target, prop, value) {
      runInAction(() => {
        target[prop as keyof T] = value;
      });
      return true;
    }
  }) as ProxyStore<T>;

  return proxyStore;
};
